// SUN-105: the Reject window for a page note. Reason is required (3–2000 after trim, same rule as the server's
// parseNotes). No pins, no questions. The writer sees the reason in My notes and gets a bell.
import { useRef, useState } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import { MOCKUP_ERRORS, type ApprovalNote } from '../types';
import { errorText } from '../mockupBoardApi';
import { pnApi } from './pageNotesApi';

const firstName = (n: string) => n.trim().split(/\s+/)[0] || n;

export function PageNoteRejectModal({ note, onClose, onDone }: { note: ApprovalNote; onClose(): void; onDone(r: Awaited<ReturnType<typeof pnApi.reject>>): void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const sending = useRef(false);

  const t = reason.trim();
  const tooShort = t.length > 0 && t.length < 3;
  const canSend = t.length >= 3 && t.length <= 2000;
  const who = firstName(note.authorName);

  const submit = async () => {
    if (!canSend || sending.current) return;
    sending.current = true; setBusy(true); setError('');
    try { onDone(await pnApi.reject(note.id, t)); }
    catch (e) { setError(errorText(e)); }
    finally { sending.current = false; setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} ariaLabel="Reject this page note" className="!max-w-md !bg-white p-6 max-sm:-m-4 max-sm:flex max-sm:!h-[100dvh] max-sm:!max-h-none max-sm:!w-[100vw] max-sm:!max-w-none max-sm:flex-col max-sm:!rounded-none max-sm:p-4">
      <>
        <h3 id="pn-reject-title" className="mb-1 text-lg font-semibold text-gray-900">Reject this page note?</h3>
        <p className="mb-3 text-sm text-gray-500">{`From ${note.authorName} (${note.authorRole}) on ${note.route}. Tell ${who} why.`}</p>
        <blockquote className="mb-3 whitespace-pre-wrap break-words rounded-lg bg-[#FAF7F0] px-3 py-2 text-sm text-gray-800">{`“${note.comment}”`}</blockquote>
        <label htmlFor="pn-reject-reason" className="mb-1 block text-sm font-medium text-gray-700">{`Reason for ${who}`}</label>
        <textarea id="pn-reject-reason" rows={4} maxLength={2000} autoFocus value={reason} onChange={e => setReason(e.target.value)}
          placeholder="Why is this note not going ahead?"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#1F4B3A] focus:outline-none focus:ring-1 focus:ring-[#1F4B3A]" />
        <p className="mt-1 text-xs text-gray-500">{`Write a reason before you reject. ${who} sees it in My notes and gets a bell.`}</p>
        {tooShort && <p className="mt-1 text-xs text-red-600">{MOCKUP_ERRORS.notesLength}</p>}
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
        <div className="mt-4 flex justify-end gap-2 max-sm:mt-auto max-sm:pt-4">
          <CancelBtn />
          <button type="button" onClick={() => void submit()} disabled={!canSend || busy}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 max-sm:min-h-[44px] max-sm:flex-1">Confirm Reject</button>
        </div>
      </>
    </Modal>
  );
}

function CancelBtn() {
  const { requestClose } = useModalClose();
  return <button type="button" onClick={requestClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 max-sm:min-h-[44px] max-sm:flex-1">Cancel</button>;
}
